import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * One-shot Firestore reads with an in-memory promise cache.
 *
 * Why:
 *   - Real-time onSnapshot listeners hold persistent WebChannel connections to
 *     firestore.googleapis.com. On flaky networks / behind ad-blockers / behind
 *     corporate proxies these stream `Listen/channel` and `Write/channel`
 *     requests time out, polluting the console and wasting battery / quota.
 *   - Public site content (hero, features, services, about, FAQ, settings) is
 *     edited from the admin panel infrequently. Visitors don't need live
 *     updates — a one-shot fetch on mount is enough.
 *
 * The cache de-duplicates concurrent calls (e.g. NewHeroSection + Honecard +
 * AboutUs all reading `homePage/content` at the same time only triggers ONE
 * network request) and survives for the lifetime of the SPA session.
 */

const docCache = new Map(); // key -> Promise<DocumentSnapshot>
const collectionCache = new Map(); // key -> Promise<QuerySnapshot>

/**
 * Fetch a document once per session.
 * @param {string} path Firestore document path, e.g. "homePage/content"
 * @returns {Promise<import('firebase/firestore').DocumentSnapshot>}
 */
export function getDocOnce(path) {
  if (!docCache.has(path)) {
    const segments = path.split('/');
    docCache.set(
      path,
      getDoc(doc(db, ...segments)).catch((err) => {
        // Don't poison the cache on transient failures.
        docCache.delete(path);
        throw err;
      })
    );
  }
  return docCache.get(path);
}

/**
 * Fetch a collection once per session.
 * @param {string} path Firestore collection path, e.g. "faqItems"
 * @returns {Promise<import('firebase/firestore').QuerySnapshot>}
 */
export function getCollectionOnce(path) {
  if (!collectionCache.has(path)) {
    collectionCache.set(
      path,
      getDocs(collection(db, path)).catch((err) => {
        collectionCache.delete(path);
        throw err;
      })
    );
  }
  return collectionCache.get(path);
}

/**
 * Manually invalidate the cache (e.g. after the admin saves new content and
 * we want the public page to refetch on next mount).
 */
export function invalidate(path) {
  docCache.delete(path);
  collectionCache.delete(path);
}
